import { parseDateOnly } from "./prismSubscriptionUtils";

export const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const SHORT_MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const MONTHLY_EXPENDITURE_COLUMN_LABELS = {
  month: "Month",
  subscriptionBreakdown: "Subscription Breakdown",
  totalSpend: "Total Spend",
};

export const formatCurrency = (value) => {
  const amount = Number(value);

  if (Number.isNaN(amount)) return "PHP 0.00";

  return `PHP ${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const getPaymentSubscriptionName = (payment) =>
  payment.subscription?.subscription_name ||
  payment.subscription_name_snapshot ||
  (payment.subscription_id
    ? `Subscription #${payment.subscription_id}`
    : "Deleted Subscription");

const getPaymentSubscriptionKey = (payment) =>
  payment.subscription_id
    ? String(payment.subscription_id)
    : `deleted:${payment.subscription_name_snapshot || payment.id}`;

export const formatSubscriptionBreakdown = (breakdown = []) =>
  Array.isArray(breakdown) && breakdown.length > 0
    ? breakdown
        .map(
          (item) =>
            `${item.subscriptionName}: ${formatCurrency(item.total)}`,
        )
        .join("\n")
    : "-";

export const buildMonthlyExpenditureData = ({
  payments = [],
  selectedYear,
  subscriptionFilter = null,
  getSubscriptionFilterValue = null,
  getSubscriptionName = getPaymentSubscriptionName,
}) => {
  const monthlyTotals = MONTH_LABELS.map((month, index) => ({
    key: month,
    month,
    shortMonth: SHORT_MONTH_LABELS[index],
    monthIndex: index,
    total: 0,
    subscriptionBreakdown: [],
  }));
  const monthlySubscriptionTotals = MONTH_LABELS.map(() => new Map());

  payments.forEach((payment) => {
    if (
      subscriptionFilter &&
      typeof getSubscriptionFilterValue === "function" &&
      getSubscriptionFilterValue(payment) !== String(subscriptionFilter)
    ) {
      return;
    }

    const paidDate = parseDateOnly(payment.date_paid);

    if (!paidDate || paidDate.getFullYear() !== selectedYear) {
      return;
    }

    const amount = Number(payment.amount);
    const normalizedAmount = Number.isNaN(amount) ? 0 : amount;
    const monthIndex = paidDate.getMonth();
    const subscriptionKeyValue =
      typeof getSubscriptionFilterValue === "function"
        ? getSubscriptionFilterValue(payment)
        : getPaymentSubscriptionKey(payment);
    const subscriptionKey = String(
      subscriptionKeyValue ?? getPaymentSubscriptionKey(payment),
    );
    const subscriptionName =
      typeof getSubscriptionName === "function"
        ? getSubscriptionName(payment)
        : getPaymentSubscriptionName(payment);
    const subscriptionTotals = monthlySubscriptionTotals[monthIndex];
    const existingSubscription = subscriptionTotals.get(subscriptionKey);

    monthlyTotals[monthIndex].total += normalizedAmount;

    if (existingSubscription) {
      existingSubscription.total += normalizedAmount;
      return;
    }

    subscriptionTotals.set(subscriptionKey, {
      key: subscriptionKey,
      subscriptionName:
        String(subscriptionName || "").trim() || "Deleted Subscription",
      total: normalizedAmount,
    });
  });

  monthlyTotals.forEach((month, monthIndex) => {
    month.subscriptionBreakdown = Array.from(
      monthlySubscriptionTotals[monthIndex].values(),
    ).sort((a, b) => {
      const nameCompare = a.subscriptionName.localeCompare(b.subscriptionName);

      return nameCompare || a.key.localeCompare(b.key);
    });
  });

  return monthlyTotals;
};

export const getTotalMonthlySpend = (monthlyExpenditureData = []) =>
  monthlyExpenditureData.reduce((total, month) => total + month.total, 0);

export const getMonthlySpendChartDimensions = ({
  compact = false,
  height: displayHeight,
} = {}) => ({
  width: compact ? 620 : 920,
  height:
    Number(displayHeight) > 0
      ? Number(displayHeight)
      : compact
        ? 240
        : 220,
});

const MonthlySpendChart = ({ data, compact = false, height: displayHeight }) => {
  const { width, height } = getMonthlySpendChartDimensions({
    compact,
    height: displayHeight,
  });
  const padding = compact
    ? {
        top: 10,
        right: 16,
        bottom: 30,
        left: 60,
      }
    : {
        top: 16,
        right: 32,
        bottom: 42,
        left: 78,
      };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const maxTotal = Math.max(...data.map((item) => item.total), 0);
  const yMax = maxTotal > 0 ? maxTotal : 1;
  const xStep = chartWidth / (data.length - 1 || 1);
  const points = data.map((item, index) => {
    const x = padding.left + index * xStep;
    const y = padding.top + chartHeight - (item.total / yMax) * chartHeight;

    return {
      ...item,
      x,
      y,
    };
  });
  const pointString = points.map((point) => `${point.x},${point.y}`).join(" ");
  const yTicks = [0, yMax / 2, yMax];

  return (
    <div style={{ overflowX: compact ? "hidden" : "auto", width: "100%" }}>
      <svg
        aria-label="Monthly expenditures line graph"
        role="img"
        viewBox={`0 0 ${width} ${height}`}
        style={{
          display: "block",
          minWidth: compact ? 0 : 720,
          width: "100%",
        }}
      >
        <line
          x1={padding.left}
          x2={padding.left}
          y1={padding.top}
          y2={padding.top + chartHeight}
          stroke="#d9d9d9"
        />
        <line
          x1={padding.left}
          x2={padding.left + chartWidth}
          y1={padding.top + chartHeight}
          y2={padding.top + chartHeight}
          stroke="#d9d9d9"
        />
        {yTicks.map((tick) => {
          const y = padding.top + chartHeight - (tick / yMax) * chartHeight;

          return (
            <g key={tick}>
              <line
                x1={padding.left}
                x2={padding.left + chartWidth}
                y1={y}
                y2={y}
                stroke="#f0f0f0"
              />
              <text
                x={padding.left - 10}
                y={y + 4}
                fill="#595959"
                fontSize={compact ? "10" : "12"}
                textAnchor="end"
              >
                {formatCurrency(tick).replace("PHP ", "")}
              </text>
            </g>
          );
        })}
        <polyline
          fill="none"
          points={pointString}
          stroke="#00573F"
          strokeWidth={compact ? "2.5" : "3"}
        />
        {points.map((point) => (
          <g key={point.month}>
            <circle cx={point.x} cy={point.y} fill="#00573F" r={compact ? 4 : 5}>
              <title>{`${point.month}: ${formatCurrency(point.total)}`}</title>
            </circle>
            <text
              x={point.x}
              y={padding.top + chartHeight + 22}
              fill="#595959"
              fontSize={compact ? "10" : "12"}
              textAnchor="middle"
            >
              {point.shortMonth}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

export default MonthlySpendChart;
